import { useEffect, useRef } from 'react'
import Button from './Button'
import './ErrorMessage.css'

export interface ErrorMessageProps {
  message: string
  onRetry?: () => void
  className?: string
}

/**
 * ErrorMessage component displays user-friendly error messages with optional retry functionality.
 * 
 * Requirements: 12.1, 12.3
 * - Displays error message without technical jargon or stack traces
 * - Retry button calls onRetry when provided
 * - Proper semantic HTML with role="alert"
 * - Accessible focus management
 */
function ErrorMessage({
  message,
  onRetry,
  className = ''
}: ErrorMessageProps) {
  const errorRef = useRef<HTMLDivElement>(null)

  // Focus management: automatically focus the error container when it mounts
  // This ensures screen readers announce the error immediately
  useEffect(() => {
    if (errorRef.current) {
      errorRef.current.focus()
    }
  }, [])

  return (
    <div
      ref={errorRef}
      className={`error-message ${className}`}
      role="alert"
      tabIndex={-1}
      aria-live="assertive"
    >
      <div className="error-message__content">
        <div className="error-message__icon" aria-hidden="true">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <p className="error-message__text">{message}</p>
      </div>
      {onRetry && (
        <div className="error-message__actions">
          <Button
            onClick={onRetry}
            variant="secondary"
            aria-label="Try again"
          >
            Retry
          </Button>
        </div>
      )}
    </div>
  )
}

export default ErrorMessage